/**
 * UsersSettings — Users management tab (Settings > Users).
 * Contains: pending approvals, team members list with role/status/permissions,
 * permissions dialog with module permissions and location restrictions.
 */
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import {
  Users, Plus, Trash, Check, Crown, X, Clock, CheckCircle, XCircle,
  ShieldCheck, Eye, PencilSimple, MapPin
} from "@phosphor-icons/react";

const getStatusBadge = (status) => {
  switch (status) {
    case "approved":
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle size={12} className="mr-1" /> Approved</Badge>;
    case "denied":
      return <Badge className="bg-red-500/10 text-red-600 border-red-500/20"><XCircle size={12} className="mr-1" /> Denied</Badge>;
    default:
      return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20"><Clock size={12} className="mr-1" /> Pending</Badge>;
  }
};

export function UsersSettings({
  users, pendingUsers, locations,
  onChangeRole, onChangeStatus, onDeleteUser,
  permDialogOpen, setPermDialogOpen,
  permUser, permForm, savingPerms,
  onOpenPermissions, onSavePermissions,
  onTogglePermission, onToggleLocationRestriction
}) {
  return (
    <>
      {/* Pending Users */}
      {pendingUsers.length > 0 && (
        <Card className="border-2 border-yellow-500/30 rounded-xl bg-yellow-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg tracking-wide flex items-center gap-2 text-yellow-700">
              <Clock size={20} weight="duotone" />
              PENDING APPROVAL ({pendingUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingUsers.map(usr => (
                <div
                  key={usr.user_id}
                  data-testid={`pending-user-${usr.user_id}`}
                  className="flex items-center justify-between p-4 bg-white rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={usr.picture} />
                      <AvatarFallback className="bg-yellow-500 text-white">
                        {usr.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{usr.name}</p>
                      <p className="text-sm text-muted-foreground">{usr.email}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => onChangeStatus(usr.user_id, "approved")}
                      data-testid={`approve-${usr.user_id}`}
                    >
                      <Check size={16} className="mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => onChangeStatus(usr.user_id, "denied")}
                      data-testid={`deny-${usr.user_id}`}
                    >
                      <X size={16} className="mr-1" />
                      Deny
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Users */}
      <Card className="border rounded-xl">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
            <Users size={20} weight="duotone" className="text-primary" />
            TEAM MEMBERS
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length > 0 ? (
            <div className="space-y-3">
              {users.map(usr => (
                <div
                  key={usr.user_id}
                  data-testid={`user-item-${usr.user_id}`}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted/30 rounded-lg gap-3"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={usr.picture} />
                      <AvatarFallback className="bg-primary text-white">
                        {usr.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{usr.name}</p>
                        {usr.role === "admin" && (
                          <Crown size={14} weight="fill" className="text-accent" />
                        )}
                        {getStatusBadge(usr.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">{usr.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-auto sm:ml-0">
                    <Select
                      value={usr.status || "pending"}
                      onValueChange={(val) => onChangeStatus(usr.user_id, val)}
                    >
                      <SelectTrigger className="w-28" data-testid={`status-select-${usr.user_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="denied">Denied</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={usr.role}
                      onValueChange={(val) => onChangeRole(usr.user_id, val)}
                    >
                      <SelectTrigger className="w-24" data-testid={`role-select-${usr.user_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => onOpenPermissions(usr)}
                      data-testid={`permissions-btn-${usr.user_id}`}
                      title="Edit Permissions"
                    >
                      <ShieldCheck size={16} className="text-primary" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDeleteUser(usr.user_id)}
                      data-testid={`delete-user-${usr.user_id}`}
                    >
                      <Trash size={16} className="text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No team members yet.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Permissions Dialog */}
      <Dialog open={permDialogOpen} onOpenChange={setPermDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-wide flex items-center gap-2">
              <ShieldCheck size={20} className="text-primary" />
              PERMISSIONS — {permUser?.name?.toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-2">
            {/* Module Permissions */}
            {[
              { key: "inventory", label: "Inventory", desc: "View counts, create new records, edit/delete existing" },
              { key: "waste_log", label: "Waste Log", desc: "View waste logs, create new entries, edit/delete existing" },
              { key: "transfers", label: "Transfers", desc: "View transfers, create new transfers, edit/delete existing" }
            ].map(mod => (
              <div key={mod.key} className="p-4 bg-muted/30 rounded-lg space-y-3">
                <p className="font-heading tracking-wide text-sm">{mod.label.toUpperCase()}</p>
                <p className="text-xs text-muted-foreground">{mod.desc}</p>
                <div className="flex gap-4 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid={`perm-${mod.key}-read`}>
                    <Switch
                      checked={permForm[mod.key]?.read ?? true}
                      onCheckedChange={() => onTogglePermission(mod.key, "read")}
                    />
                    <span className="text-sm flex items-center gap-1">
                      <Eye size={14} /> View
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-testid={`perm-${mod.key}-write`}>
                    <Switch
                      checked={permForm[mod.key]?.write ?? true}
                      onCheckedChange={() => onTogglePermission(mod.key, "write")}
                    />
                    <span className="text-sm flex items-center gap-1">
                      <Plus size={14} /> Create
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-testid={`perm-${mod.key}-edit`}>
                    <Switch
                      checked={permForm[mod.key]?.edit ?? true}
                      onCheckedChange={() => onTogglePermission(mod.key, "edit")}
                    />
                    <span className="text-sm flex items-center gap-1">
                      <PencilSimple size={14} /> Edit
                    </span>
                  </label>
                </div>
              </div>
            ))}

            {/* Location Restrictions */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-3">
              <p className="font-heading tracking-wide text-sm flex items-center gap-2">
                <MapPin size={16} className="text-primary" />
                LOCATION ACCESS
              </p>
              <p className="text-xs text-muted-foreground">
                Leave all unchecked to allow access to all locations. Check specific locations to restrict access.
              </p>
              <div className="space-y-2 mt-2">
                {locations.map(loc => (
                  <label
                    key={loc.location_id}
                    className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-muted/50 transition-colors"
                    data-testid={`perm-location-${loc.location_id}`}
                  >
                    <Checkbox
                      checked={(permForm.allowed_locations || []).includes(loc.location_id)}
                      onCheckedChange={() => onToggleLocationRestriction(loc.location_id)}
                    />
                    <span className="text-sm">{loc.name}</span>
                  </label>
                ))}
              </div>
              {(permForm.allowed_locations || []).length > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Restricted to {permForm.allowed_locations.length} location(s)
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setPermDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={onSavePermissions}
              className="bg-primary"
              disabled={savingPerms}
              data-testid="save-permissions-btn"
            >
              <Check size={16} className="mr-2" />
              {savingPerms ? "Saving..." : "Save Permissions"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
